import isNil from "lodash/isNil";
import React, { useCallback, useEffect, useState } from "react";
import ErrorLabel from "ui/atoms/ErrorLabel";

import { QuestionTitle } from "./Text";

const numericRegExp = /^-?\d*\.?\d*$/;

function isValueValid(value) {
  return !isNil(value) && numericRegExp.test(value);
}

function NumericText({
  hasError,
  disabled,
  willShowNotAllowedCursor,
  onChange,
  isOfficeUse,
  value,
  label,
  errorLabel,
  maxValue,
  minValue,
  integerOnly,
  placeholder,
  isRequired,
  $small,
  $medium,
  $large,
  ...props
}) {
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    setInputValue((prev) => (isValueValid(value) ? `${value}` : prev));
  }, [value]);

  const updateInputValue = useCallback(
    (value) => {
      const willUpdate = isValueValid(value);
      if (willUpdate) {
        const stringValue = `${value}`;
        setInputValue(stringValue);
        onChange(stringValue);
      }
    },
    [onChange],
  );

  let classes =
    "form-input focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)] border block transition duration-150 ease-in-out sm:text-sm sm:leading-5";

  if (willShowNotAllowedCursor) {
    classes += " cursor-not-allowed bg-gray-100";
  }

  if (hasError) {
    classes += " border-red-500";
  }

  if ($small) {
    classes += " w-10";
  } else if ($medium) {
    classes += " w-15";
  } else if ($large) {
    classes += " w-22";
  } else {
    classes += " w-full";
  }

  const handleChange = (e) => {
    if (disabled) return;
    const targetValue = e.target.value;
    if (targetValue === "") return updateInputValue(targetValue);

    // Remove all non numeric characters and non dots
    if (!numericRegExp.test(targetValue)) {
      return updateInputValue(targetValue.replace(/[^0-9.-]/g, ""));
    }
    // Remove final dot 10.. -> 10.
    if (targetValue.endsWith("..")) {
      return updateInputValue(targetValue.slice(0, -1));
    }
    // Remove more than one dot 10.0. -> 10.0
    if (targetValue.split(".").length > 2) {
      return updateInputValue(targetValue.slice(0, -1));
    }

    if (integerOnly && !Number.isInteger(+targetValue)) return;
    if (!isNil(maxValue) && +targetValue > maxValue) return;
    if (!isNil(minValue) && +targetValue < minValue) return;
    updateInputValue(targetValue);
  };

  const handleBlur = () => {
    if (inputValue.endsWith(".")) {
      updateInputValue(inputValue.slice(0, -1));
    }
  };

  return (
    <>
      {label && (
        <QuestionTitle isOfficeUse={isOfficeUse} isRequired={isRequired}>
          {label}
        </QuestionTitle>
      )}
      <div className="relative">
        <input
          className={classes}
          disabled={disabled}
          name={label}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={placeholder}
          value={inputValue}
          {...props}
        />
      </div>
      {hasError && <ErrorLabel>{errorLabel}</ErrorLabel>}
    </>
  );
}

export default NumericText;
